import React, { useEffect } from 'react';
import { FaUndo, FaClock, FaCreditCard, FaInfoCircle } from 'react-icons/fa';

const RefundPolicy = () => {
  useEffect(() => {
    document.title = "Return & Refund Policy - SastaMastaRide";
  }, []);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
        <div className="text-center mb-8">
          <FaUndo className="text-blue-600 text-5xl mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">Return & Refund Policy</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="prose prose-lg max-w-none text-gray-700">
          <div className="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-600">
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> This website is operated by <strong>SastaMastaRide</strong>, managed by <strong>RKTECH Solutions</strong>.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Overview</h2>
            <p className="mb-4">
              At SastaMastaRide, we strive to provide the best ridesharing experience. This Return & Refund Policy 
              outlines the terms and conditions for refunds related to ride bookings, cancellations, and service issues.
            </p>
            <p>
              By using our platform, you agree to this refund policy. Please read it carefully before making a booking.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Refund Eligibility</h2>
            <p className="mb-4">Refunds may be issued in the following circumstances:</p>
            <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
              <li>Rider cancels the trip before departure (subject to cancellation policy)</li>
              <li>Passenger cancels within the allowed cancellation window</li>
              <li>Ride is cancelled by the platform due to safety or policy violations</li>
              <li>Technical issues prevent the ride from being completed</li>
              <li>Rider fails to show up at the pickup location</li>
              <li>Significant deviation from the agreed route without passenger consent</li>
              <li>Service quality issues that significantly impact the ride experience</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Refund Duration & Processing Time</h2>
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <div className="flex items-start space-x-3 mb-4">
                <FaClock className="text-blue-600 text-2xl mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Refund Processing Timeline</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li><strong>Refund Request Processing:</strong> 24-48 hours from the time of request</li>
                    <li><strong>Refund Approval:</strong> Within 3-5 business days after verification</li>
                    <li><strong>Refund Credit to Account:</strong> 5-7 business days</li>
                    <li><strong>Refund to Original Payment Method:</strong> 7-14 business days</li>
                    <li><strong>Bank Transfer Refunds:</strong> 10-15 business days</li>
                  </ul>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> Processing times may vary depending on your payment method and bank. 
              International transactions may take longer.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Refund Modes</h2>
            <div className="bg-gray-50 p-6 rounded-lg mb-4">
              <div className="flex items-start space-x-3">
                <FaCreditCard className="text-green-600 text-2xl mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Available Refund Methods</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">1. Original Payment Method (Preferred)</h4>
                      <ul className="list-disc list-inside ml-4 space-y-1 text-gray-700">
                        <li>Credit/Debit Cards: Refunded to the same card used for payment</li>
                        <li>UPI: Refunded to the same UPI ID used for payment</li>
                        <li>Net Banking: Refunded to the same bank account</li>
                        <li>Digital Wallets: Refunded to the same wallet (Paytm, PhonePe, etc.)</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">2. Platform Wallet Credit</h4>
                      <p className="text-gray-700">
                        Refunds can be credited to your SastaMastaRide wallet for instant use on future bookings. 
                        Wallet credits do not expire.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">3. Bank Transfer (NEFT/IMPS)</h4>
                      <p className="text-gray-700">
                        For amounts above ₹500, refunds can be processed via direct bank transfer. 
                        You will need to provide your bank account details.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Refund Amount Calculation</h2>
            <div className="bg-yellow-50 p-6 rounded-lg mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Refund Structure</h3>
              <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
                <li><strong>Full Refund:</strong> Cancellation within 2 hours of booking or 24 hours before trip departure</li>
                <li><strong>Partial Refund (80%):</strong> Cancellation 12-24 hours before trip departure</li>
                <li><strong>Partial Refund (50%):</strong> Cancellation 6-12 hours before trip departure</li>
                <li><strong>No Refund:</strong> Cancellation less than 6 hours before departure or no-show</li>
                <li><strong>Platform Fee:</strong> Non-refundable (typically 10-15% of ride fare)</li>
              </ul>
            </div>
            <p className="text-sm text-gray-600">
              <strong>Exception:</strong> Full refund (including platform fee) is provided if the cancellation is due to 
              platform error, rider no-show, or safety concerns.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. How to Request a Refund</h2>
            <ol className="list-decimal list-inside ml-4 space-y-2 text-gray-700">
              <li>Log in to your SastaMastaRide account</li>
              <li>Go to "My Bookings" section</li>
              <li>Select the booking for which you want a refund</li>
              <li>Click on "Request Refund" or "Cancel Booking"</li>
              <li>Select the reason for cancellation/refund</li>
              <li>Choose your preferred refund method</li>
              <li>Submit the request</li>
            </ol>
            <p className="mt-4 text-gray-700">
              Alternatively, you can contact our support team at{' '}
              <a href="mailto:support@sastamastaride.com" className="text-blue-600 hover:underline">
                support@sastamastaride.com
              </a>{' '}
              or call us at <a href="tel:+911800XXXXXX" className="text-blue-600 hover:underline">+91-1800-XXX-XXXX</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Non-Refundable Items</h2>
            <ul className="list-disc list-inside ml-4 space-y-2 text-gray-700">
              <li>Platform service fee (unless cancellation is due to platform error)</li>
              <li>Completed rides (no refunds after trip completion)</li>
              <li>Rides cancelled due to passenger no-show</li>
              <li>Rides cancelled less than 6 hours before departure</li>
              <li>Any additional services or add-ons that have been utilized</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Dispute Resolution</h2>
            <p className="mb-4 text-gray-700">
              If you are not satisfied with a refund decision, you can:
            </p>
            <ol className="list-decimal list-inside ml-4 space-y-2 text-gray-700">
              <li>Contact our customer support team for review</li>
              <li>Provide additional documentation or evidence if required</li>
              <li>Escalate to our dispute resolution team</li>
              <li>File a complaint with consumer forums if the issue remains unresolved</li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Contact Information</h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-2">
                For refund-related queries, please contact:
              </p>
              <p className="text-gray-700"><strong>Email:</strong> refunds@sastamastaride.com</p>
              <p className="text-gray-700"><strong>Support:</strong> support@sastamastaride.com</p>
              <p className="text-gray-700"><strong>Phone:</strong> +91-1800-XXX-XXXX</p>
              <p className="text-gray-700"><strong>Address:</strong> [Your Registered Address], India</p>
            </div>
          </section>

          <div className="bg-blue-50 p-6 rounded-lg mt-8">
            <div className="flex items-start space-x-3">
              <FaInfoCircle className="text-blue-600 text-2xl mt-1" />
              <div>
                <p className="text-gray-700">
                  <strong>Important:</strong> This refund policy is subject to change. We recommend reviewing it 
                  periodically. Continued use of our platform after policy changes constitutes acceptance of the new terms.
                </p>
                <p className="text-gray-700 mt-2">
                  For questions about this policy, contact us at{' '}
                  <a href="mailto:legal@sastamastaride.com" className="text-blue-600 hover:underline">
                    legal@sastamastaride.com
                  </a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;

